'use client';

import { Legend } from 'recharts';

import { cn } from '../../../lib';

import { useChart } from './ChartContainer';
import { getPayloadConfigFromPayload } from './utils';

export const ChartLegend = Legend;

// Lightweight typing for the legend payload we actually use.
type TLegendItem = {
  // Recharts LegendPayload.color is optional.
  color?: string;
  dataKey?: string | number | ((obj: any) => any);
  // Recharts LegendPayload.value can be string | number | undefined,
  // so we allow undefined here to stay assignment-compatible.
  value: string | number | undefined;
};

interface IChartLegendContentProps {
  className?: string;
  hideIcon?: boolean;
  nameKey?: string;
  // These are provided by Recharts Legend when used as a content renderer.
  // They are optional so this component can also be rendered directly.
  payload?: readonly TLegendItem[];
  verticalAlign?: 'top' | 'middle' | 'bottom';
}

/**
 * -----------------------------------------------------------------------------
 * Custom component to display the legend content.
 */
export function ChartLegendContent({
  className,
  hideIcon = false,
  nameKey,
  payload,
  verticalAlign = 'bottom',
}: IChartLegendContentProps) {
  const { config } = useChart();

  if (!payload?.length) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-4',
        verticalAlign === 'top' ? 'pb-3' : 'pt-3',
        className
      )}
    >
      {payload.map((item) => {
        const key = `${nameKey || item.dataKey || 'value'}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);

        return (
          <div
            key={item.value}
            className={cn(
              '[&>svg]:text-muted-foreground flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3'
            )}
          >
            {itemConfig?.icon && !hideIcon ? (
              <itemConfig.icon />
            ) : (
              <div
                className="size-2 shrink-0 rounded-[2px]"
                style={{
                  backgroundColor: item.color,
                }}
              />
            )}
            {itemConfig?.label}
          </div>
        );
      })}
    </div>
  );
}

